import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Form } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import NotificationBoxPage from '../components/notificationBoxPage'
import FullLogo from '../assets/logos/full-logo.png'
import LanguagesDropdown from '../components/languageDropdown'
import theme from '../constants/theme'
import apiOfferLetters from '../services/api/offerLetter'
import storageApi from '../services/firebaseStorage'
import Briefcase from '../assets/svgs/Briefcase'
import GeoAlt from '../assets/svgs/GeoAlt'
import CalendarOutline from '../assets/svgs/CalendarOutline'
import InfoCircle from '../assets/svgs/InfoCircle'
import FileEarmarkText from '../assets/svgs/FileEarmarkText'
import ArrowRight from '../assets/svgs/ArrowRight'
import Check from '../assets/svgs/Check'
import X from '../assets/svgs/X'
import Lock from '../assets/svgs/Lock'
import {
  OfferPage,
  OfferScroll,
  OfferContent,
  HeroBanner,
  HeroRing,
  HeroInner,
  HeroBadge,
  HeroTitle,
  HeroMetaRow,
  HeroMetaItem,
  StatusCard,
  StatusLeft,
  StatusTitle,
  StatusSubtitle,
  CountdownRow,
  CountdownUnit,
  CountdownValue,
  CountdownLabel,
  SectionCard,
  CardHeadingRow,
  CardHeading,
  SoftDivider,
  GreetingRow,
  AvatarCircle,
  GreetingName,
  GreetingFrom,
  LetterBody,
  DetailsGrid,
  DetailLabel,
  DetailValue,
  AttachmentBox,
  AttachmentTopRow,
  FileIconWrapper,
  FileName,
  FileMeta,
  ViewLink,
  NoteBox,
  NoteLabel,
  NoteText,
  DecisionRow,
  ActionButton,
  LinkButton,
  FlowPanel,
  FlowTitle,
  FlowHelp,
  FlowActions,
  FieldLabel,
  RequiredMark,
  FieldInput,
  FieldHint,
  FieldError,
  ProposedFieldsScroll,
  SecureFooter,
} from './offerResponse.style'

const ACTION_STATES = {
  IDLE: 'idle',
  ACCEPT: 'accept',
  DECLINE: 'decline',
  NEGOTIATE: 'negotiate',
}

const OFFER_RESPONSE_DATE_LOCALE = { ar: 'ar', gr: 'de-DE' }

const HERO_RINGS = [
  { size: 150, opacity: 0.1 },
  { size: 220, opacity: 0.075 },
  { size: 300, opacity: 0.05 },
]

const COUNTDOWN_TICK_MS = 30000

function OfferResponse() {
  const { t, i18n } = useTranslation()
  const { key } = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [context, setContext] = useState(null)
  const [actionState, setActionState] = useState(ACTION_STATES.IDLE)
  const [joiningDate, setJoiningDate] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [signedDocument, setSignedDocument] = useState(null)
  const [signedDocumentError, setSignedDocumentError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [negotiationReason, setNegotiationReason] = useState('')
  const [proposedValues, setProposedValues] = useState({})
  const [showProposedFields, setShowProposedFields] = useState(false)
  const [nowTick, setNowTick] = useState(() => Date.now())

  const NEGOTIABLE_FIELDS = [
    {
      key: 'net_salary',
      label: t('offerResponse.field.netSalary', 'Net Salary'),
    },
    {
      key: 'gross_salary',
      label: t('offerResponse.field.grossSalary', 'Gross Salary'),
    },
    {
      key: 'compensation_amount',
      label: t('offerResponse.field.compensationAmount', 'Compensation Type'),
    },
    {
      key: 'expected_joining_date',
      label: t('offerResponse.field.joiningDate', 'Expected Joining Date'),
    },
    { key: 'currency', label: t('offerResponse.field.currency', 'Currency') },
    {
      key: 'employment_type',
      label: t('offerResponse.field.employmentType', 'Employment Type'),
    },
    {
      key: 'contract_duration',
      label: t('offerResponse.field.contractDuration', 'Contract Duration'),
    },
    {
      key: 'contract_duration_unit',
      label: t('offerResponse.field.contractDurationUnit', 'Duration Unit'),
    },
    {
      key: 'medical_insurance',
      label: t('offerResponse.field.medicalInsurance', 'Medical Insurance'),
    },
    {
      key: 'life_insurance',
      label: t('offerResponse.field.lifeInsurance', 'Life Insurance'),
    },
    {
      key: 'social_insurance',
      label: t('offerResponse.field.socialInsurance', 'Social Insurance'),
    },
  ]

  useEffect(() => {
    const load = async () => {
      try {
        const response = await apiOfferLetters.getCandidateResponseContext(key)
        setContext(response)
        if (response?.candidateJoiningDate)
          setJoiningDate(response.candidateJoiningDate)
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            t('offerResponse.invalidOrExpiredLink'),
        )
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [key, t])

  // keeps the expiry countdown in the status strip fresh
  useEffect(() => {
    const interval = setInterval(
      () => setNowTick(Date.now()),
      COUNTDOWN_TICK_MS,
    )
    return () => clearInterval(interval)
  }, [])

  const isInitialOffer = Boolean(context?.isInitialOffer)

  const canSubmitAccept = useMemo(
    () =>
      !!joiningDate && (isInitialOffer || !!signedDocument) && !isSubmitting,
    [joiningDate, signedDocument, isSubmitting, isInitialOffer],
  )

  const countdown = useMemo(() => {
    if (!context?.offerExpiryDate) return null
    const remaining = new Date(context.offerExpiryDate).getTime() - nowTick
    if (!Number.isFinite(remaining) || remaining <= 0) return null
    return {
      days: Math.floor(remaining / 86400000),
      hours: Math.floor((remaining % 86400000) / 3600000),
      minutes: Math.floor((remaining % 3600000) / 60000),
    }
  }, [context?.offerExpiryDate, nowTick])

  const initials = useMemo(() => {
    const parts = (context?.candidateName || '').trim().split(/\s+/)
    return parts
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('')
  }, [context?.candidateName])

  const submit = async (action) => {
    setIsSubmitting(true)
    try {
      let payload
      if (action === 'approve') {
        let blobName = null
        if (!isInitialOffer) {
          blobName = await storageApi.uploadFile(
            signedDocument,
            () => {},
            null,
            null,
          )
        }
        payload = {
          action: 'approve',
          joining_date: joiningDate,
          ...(blobName && { signed_document_url: blobName }),
        }
      } else if (action === 'negotiate') {
        const proposed_values = Object.fromEntries(
          Object.entries(proposedValues).filter(([, v]) => v?.trim()),
        )
        payload = {
          action: 'negotiate',
          reason: negotiationReason,
          ...(Object.keys(proposed_values).length > 0 && { proposed_values }),
        }
      } else {
        payload = { action: 'reject', rejection_reason: rejectionReason }
      }
      await apiOfferLetters.submitCandidateResponse(key, payload)
      setResult(
        action === 'approve'
          ? 'approved'
          : action === 'negotiate'
            ? 'negotiated'
            : 'rejected',
      )
    } catch (err) {
      setError(err?.response?.data?.message || t('offerResponse.submitFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSignedDocumentChange = (e) => {
    const file = e.target.files?.[0]
    setSignedDocumentError('')
    if (!file) {
      setSignedDocument(null)
      return
    }
    if (file.type !== 'application/pdf') {
      setSignedDocumentError(t('offerResponse.signedDocumentInvalidFormat'))
      setSignedDocument(null)
      e.target.value = ''
      return
    }
    setSignedDocument(file)
  }

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('general.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) return <NotificationBoxPage messageType="error" message={error} />

  if (result === 'approved') {
    return (
      <NotificationBoxPage
        messageType="success"
        message={t('offerResponse.acceptedSuccess')}
      />
    )
  }

  if (result === 'rejected') {
    return (
      <NotificationBoxPage
        messageType="success"
        message={t('offerResponse.rejectedSuccess')}
      />
    )
  }

  if (result === 'negotiated' || context?.candidateResponse === 'Negotiation') {
    return (
      <NotificationBoxPage
        messageType="info"
        message={t(
          'offerResponse.negotiationUnderReview',
          'Your negotiation request has been submitted and is under review.',
        )}
      />
    )
  }

  if (context?.alreadyResponded) {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('offerResponse.alreadyResponded')}
      />
    )
  }

  const dateLocale = OFFER_RESPONSE_DATE_LOCALE[i18n.language] ?? 'en-US'
  const formatDate = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const expiryLabel = context?.offerExpiryDate
    ? new Date(context.offerExpiryDate).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  const jobLocation = context?.jobLocation || context?.location
  const startDate = formatDate(
    context?.expectedJoiningDate || context?.candidateJoiningDate,
  )

  const heroMeta = [
    context?.companyName && {
      id: 'company',
      icon: <Briefcase width="13" height="13" color="rgba(255,255,255,0.78)" />,
      text: context.companyName,
    },
    jobLocation && {
      id: 'location',
      icon: <GeoAlt width="13" height="13" color="rgba(255,255,255,0.78)" />,
      text: jobLocation,
    },
    startDate && {
      id: 'startDate',
      icon: (
        <CalendarOutline
          width={13}
          height={13}
          color="rgba(255,255,255,0.78)"
        />
      ),
      text: t('offerResponse.startsOn', { date: startDate }),
    },
  ].filter(Boolean)

  const offerDetails = [
    startDate && {
      id: 'startDate',
      label: t('offerResponse.detail.startDate', 'Start date'),
      value: startDate,
    },
    context?.employmentType && {
      id: 'employment',
      label: t('offerResponse.detail.employment', 'Employment'),
      value: context.employmentType,
    },
    jobLocation && {
      id: 'location',
      label: t('offerResponse.detail.location', 'Location'),
      value: jobLocation,
    },
    context?.reportingTo && {
      id: 'reportingTo',
      label: t('offerResponse.detail.reportingTo', 'Reporting to'),
      value: context.reportingTo,
    },
  ].filter(Boolean)

  const hasDocuments = Boolean(context?.offerUrl || context?.attachmentUrl)

  return (
    <OfferPage>
      <Row
        style={{
          position: 'sticky',
          top: 0,

          zIndex: 1000,
          backgroundColor: theme.white,
          padding: '0px 20px',
        }}
      >
        <Col xs="auto">
          <img height="auto" width="150px" src={FullLogo} alt="" />
        </Col>
        <Col />
        <Col xs="auto" className="mt-3">
          <LanguagesDropdown />
        </Col>
      </Row>

      <OfferScroll>
        <OfferContent>
          {/* Hero */}
          <HeroBanner>
            {HERO_RINGS.map(({ size, opacity }) => (
              <HeroRing
                key={size}
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  border: `1.5px solid rgba(255,255,255,${opacity})`,
                }}
              />
            ))}
            <HeroInner>
              <HeroBadge>
                <FileEarmarkText
                  width="11"
                  height="11"
                  color="rgba(255,255,255,0.92)"
                />
                {t('offerResponse.jobOffer')}
              </HeroBadge>
              <HeroTitle>{context?.positionTitle}</HeroTitle>
              {heroMeta.length > 0 && (
                <HeroMetaRow>
                  {heroMeta.map(({ id, icon, text }) => (
                    <HeroMetaItem key={id}>
                      {icon}
                      {text}
                    </HeroMetaItem>
                  ))}
                </HeroMetaRow>
              )}
            </HeroInner>
          </HeroBanner>

          {/* Awaiting response + expiry countdown */}
          <StatusCard>
            <StatusLeft>
              <InfoCircle width={17} height={17} color={theme.talentinoBlue} />
              <div>
                <StatusTitle>
                  {t(
                    'offerResponse.awaitingResponse',
                    'This offer is awaiting your response',
                  )}
                </StatusTitle>
                {expiryLabel && (
                  <StatusSubtitle>
                    {t('offerResponse.expiryValidUntil')}
                    {expiryLabel}
                    {t('offerResponse.expiryRespondBefore')}
                  </StatusSubtitle>
                )}
              </div>
            </StatusLeft>
            {countdown && (
              <CountdownRow>
                <CountdownUnit>
                  <CountdownValue>{countdown.days}</CountdownValue>
                  <CountdownLabel>
                    {t('offerResponse.daysShort', 'Days')}
                  </CountdownLabel>
                </CountdownUnit>
                <CountdownUnit>
                  <CountdownValue>{countdown.hours}</CountdownValue>
                  <CountdownLabel>
                    {t('offerResponse.hoursShort', 'Hrs')}
                  </CountdownLabel>
                </CountdownUnit>
                <CountdownUnit>
                  <CountdownValue>{countdown.minutes}</CountdownValue>
                  <CountdownLabel>
                    {t('offerResponse.minutesShort', 'Min')}
                  </CountdownLabel>
                </CountdownUnit>
              </CountdownRow>
            )}
          </StatusCard>

          <Row className="g-3 mt-2">
            {/* Offer letter body */}
            <Col xs={12} lg={hasDocuments ? 7 : 12}>
              <SectionCard>
                <GreetingRow>
                  {initials && <AvatarCircle>{initials}</AvatarCircle>}
                  <div style={{ minWidth: 0 }}>
                    <GreetingName>
                      {t('offerResponse.dearCandidate', {
                        name: context?.candidateName,
                      })}
                    </GreetingName>
                    {context?.companyName && (
                      <GreetingFrom>
                        {t('offerResponse.fromCompany', {
                          company: context.companyName,
                        })}
                      </GreetingFrom>
                    )}
                  </div>
                </GreetingRow>

                <LetterBody>
                  {t('offerResponse.offerBodyPart1')}
                  <strong>{context?.positionTitle}</strong>
                  {context?.companyName
                    ? t('offerResponse.atCompany', {
                        company: context.companyName,
                      })
                    : ''}
                  {t('offerResponse.offerBodyPart2')}
                </LetterBody>

                {offerDetails.length > 0 && (
                  <>
                    <SoftDivider />
                    <DetailsGrid>
                      {offerDetails.map(({ id, label, value }) => (
                        <div key={id}>
                          <DetailLabel>{label}</DetailLabel>
                          <DetailValue>{value}</DetailValue>
                        </div>
                      ))}
                    </DetailsGrid>
                  </>
                )}

                {context?.recruiterNote && (
                  <>
                    <SoftDivider />
                    <NoteBox accentcolor="#7c3aed" tint="#F5F3FF">
                      <NoteLabel accentcolor="#7c3aed">
                        {t(
                          'offerResponse.recruiterNote',
                          'Note from recruiter',
                        )}
                      </NoteLabel>
                      <NoteText color="#4c1d95">
                        {context.recruiterNote}
                      </NoteText>
                    </NoteBox>
                  </>
                )}
              </SectionCard>
            </Col>

            {/* Documents */}
            {hasDocuments && (
              <Col xs={12} lg={5}>
                <SectionCard>
                  {context?.offerUrl && (
                    <>
                      <CardHeadingRow>
                        <CardHeading>
                          {t('offerResponse.offerLetterTitle', 'Offer Letter')}
                        </CardHeading>
                      </CardHeadingRow>
                      <AttachmentCard
                        label={t('offerResponse.offerLetterFileName', {
                          name:
                            context?.candidateName?.replace(/\s/g, '_') || '',
                        })}
                        description={t('offerResponse.offerLetterDescription')}
                        url={context.offerUrl}
                        accentColor={theme.talentinoSpecialBlue}
                      />
                    </>
                  )}

                  {context?.attachmentUrl && (
                    <>
                      <CardHeadingRow
                        className={context?.offerUrl ? 'mt-4' : undefined}
                      >
                        <CardHeading>
                          {t(
                            'offerResponse.additionalDocumentTitle',
                            'Additional Document',
                          )}
                        </CardHeading>
                      </CardHeadingRow>
                      <AttachmentCard
                        label={t('offerResponse.supplementaryFileName')}
                        description={t(
                          'offerResponse.supplementaryDescription',
                        )}
                        url={context.attachmentUrl}
                        accentColor={theme.talentinoSecondaryGray}
                      />
                    </>
                  )}
                </SectionCard>
              </Col>
            )}
          </Row>

          {/* Decision */}
          <div className="mt-3">
            <SectionCard>
              <CardHeadingRow>
                <CardHeading>
                  {t('offerResponse.yourDecision', 'Your decision')}
                </CardHeading>
              </CardHeadingRow>

              {/* Action Buttons - initial state */}
              {actionState === ACTION_STATES.IDLE && (
                <>
                  <StatusSubtitle className="mb-3">
                    {t('offerResponse.reviewDecisionPrompt')}
                  </StatusSubtitle>
                  <DecisionRow>
                    <ActionButton
                      type="button"
                      tone="primary"
                      onClick={() => setActionState(ACTION_STATES.ACCEPT)}
                    >
                      <Check width={15} height={15} color={theme.white} />
                      {t('offerResponse.acceptOffer')}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      tone="danger"
                      onClick={() => setActionState(ACTION_STATES.DECLINE)}
                    >
                      <X width={15} height={15} color={theme.talentinoRed} />
                      {t('offerResponse.declineOffer')}
                    </ActionButton>
                    {context?.allowNegotiation && (
                      <ActionButton
                        type="button"
                        tone="violet"
                        onClick={() => setActionState(ACTION_STATES.NEGOTIATE)}
                      >
                        {t('offerResponse.negotiateOffer', 'Negotiate')}
                      </ActionButton>
                    )}
                  </DecisionRow>
                </>
              )}

              {/* Accept flow - joining date step */}
              {actionState === ACTION_STATES.ACCEPT && (
                <FlowPanel accentcolor={theme.talentinoGreen} tint="#F0F9F4">
                  <FlowTitle color={theme.talentinoGreen}>
                    {t('offerResponse.acceptingOfferTitle')}
                  </FlowTitle>
                  <FlowHelp>
                    {isInitialOffer
                      ? t('offerResponse.acceptingInitialOfferHelp')
                      : t('offerResponse.acceptingOfferHelp')}
                  </FlowHelp>

                  {isInitialOffer && (
                    <NoteBox
                      className="mt-3"
                      accentcolor="#F59E0B"
                      tint="#FFF8E1"
                    >
                      <NoteLabel accentcolor="#92400E">
                        {t('offerResponse.initialOfferTitle')}
                      </NoteLabel>
                      <NoteText color="#78350F">
                        {t('offerResponse.initialOfferDisclaimer')}
                      </NoteText>
                    </NoteBox>
                  )}

                  <Form.Group className="mt-3 mb-3">
                    <FieldLabel>
                      {t('offerResponse.joiningDateLabel')}
                    </FieldLabel>
                    <FieldInput
                      type="date"
                      value={joiningDate}
                      onChange={(e) => setJoiningDate(e.target.value)}
                      style={{ maxWidth: '250px' }}
                    />
                  </Form.Group>
                  {!isInitialOffer && (
                    <Form.Group className="mb-3">
                      <FieldLabel>
                        {t('offerResponse.signedDocumentLabel')}
                        <RequiredMark>*</RequiredMark>
                      </FieldLabel>
                      <FieldInput
                        type="file"
                        accept="application/pdf"
                        onChange={handleSignedDocumentChange}
                        style={{ maxWidth: '350px' }}
                        disabled={isSubmitting}
                      />
                      {signedDocumentError && (
                        <FieldError>{signedDocumentError}</FieldError>
                      )}
                      <FieldHint>
                        {t('offerResponse.signedDocumentHelp')}
                      </FieldHint>
                    </Form.Group>
                  )}
                  <FlowActions>
                    <ActionButton
                      type="button"
                      tone="accept"
                      onClick={() => submit('approve')}
                      disabled={!canSubmitAccept}
                    >
                      {isSubmitting
                        ? t('offerResponse.submitting')
                        : t('offerResponse.confirmAcceptance')}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      tone="neutral"
                      onClick={() => {
                        setActionState(ACTION_STATES.IDLE)
                        setJoiningDate(context?.candidateJoiningDate || '')
                        setSignedDocument(null)
                        setSignedDocumentError('')
                      }}
                      disabled={isSubmitting}
                    >
                      {t('general.back')}
                    </ActionButton>
                  </FlowActions>
                </FlowPanel>
              )}

              {/* Decline flow - rejection reason step */}
              {actionState === ACTION_STATES.DECLINE && (
                <FlowPanel
                  accentcolor={theme.talentinoDarkerRed}
                  tint="#FDF2F2"
                >
                  <FlowTitle color={theme.talentinoDarkerRed}>
                    {t('offerResponse.decliningOfferTitle')}
                  </FlowTitle>
                  <FlowHelp>{t('offerResponse.decliningOfferHelp')}</FlowHelp>
                  <Form.Group className="mt-3 mb-3">
                    <FieldLabel>{t('offerResponse.reasonLabel')}</FieldLabel>
                    <FieldInput
                      as="textarea"
                      rows={3}
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder={t('offerResponse.reasonPlaceholder')}
                    />
                  </Form.Group>
                  <FlowActions>
                    <ActionButton
                      type="button"
                      tone="decline"
                      onClick={() => submit('reject')}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t('offerResponse.submitting')
                        : t('offerResponse.confirmDecline')}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      tone="neutral"
                      onClick={() => setActionState(ACTION_STATES.IDLE)}
                      disabled={isSubmitting}
                    >
                      {t('general.back')}
                    </ActionButton>
                  </FlowActions>
                </FlowPanel>
              )}

              {/* Negotiate flow */}
              {actionState === ACTION_STATES.NEGOTIATE && (
                <FlowPanel accentcolor="#7c3aed" tint="#F5F3FF">
                  <FlowTitle color="#7c3aed">
                    {t(
                      'offerResponse.negotiatingOfferTitle',
                      'Negotiate Offer Terms',
                    )}
                  </FlowTitle>
                  <FlowHelp>
                    {t(
                      'offerResponse.negotiatingOfferHelp',
                      'Share your reason for negotiating and optionally propose specific values for any terms.',
                    )}
                  </FlowHelp>
                  <Form.Group className="mt-3 mb-3">
                    <FieldLabel>
                      {t(
                        'offerResponse.negotiationReasonLabel',
                        'Reason for negotiation',
                      )}
                      <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FieldInput
                      as="textarea"
                      rows={3}
                      value={negotiationReason}
                      onChange={(e) => setNegotiationReason(e.target.value)}
                      placeholder={t(
                        'offerResponse.negotiationReasonPlaceholder',
                        'Explain why you would like to negotiate...',
                      )}
                    />
                  </Form.Group>
                  <LinkButton
                    type="button"
                    className="mb-3"
                    onClick={() => setShowProposedFields((prev) => !prev)}
                  >
                    {showProposedFields
                      ? t(
                          'offerResponse.hideProposedValues',
                          '− Hide proposed values',
                        )
                      : t(
                          'offerResponse.showProposedValues',
                          '+ Propose specific values (optional)',
                        )}
                  </LinkButton>
                  {showProposedFields && (
                    <ProposedFieldsScroll>
                      {NEGOTIABLE_FIELDS.map(({ key, label }) => (
                        <Form.Group key={key} className="mb-3">
                          <FieldLabel>{label}</FieldLabel>
                          <FieldInput
                            type="text"
                            value={proposedValues[key] || ''}
                            onChange={(e) =>
                              setProposedValues((prev) => ({
                                ...prev,
                                [key]: e.target.value,
                              }))
                            }
                            placeholder={t(
                              'offerResponse.proposedValuePlaceholder',
                              'Leave blank to skip',
                            )}
                            style={{ maxWidth: '300px' }}
                          />
                        </Form.Group>
                      ))}
                    </ProposedFieldsScroll>
                  )}
                  <FlowActions>
                    <ActionButton
                      type="button"
                      tone="submitViolet"
                      onClick={() => submit('negotiate')}
                      disabled={!negotiationReason.trim() || isSubmitting}
                    >
                      {isSubmitting
                        ? t('offerResponse.submitting')
                        : t(
                            'offerResponse.submitNegotiation',
                            'Submit Negotiation',
                          )}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      tone="neutral"
                      onClick={() => {
                        setActionState(ACTION_STATES.IDLE)
                        setNegotiationReason('')
                        setProposedValues({})
                        setShowProposedFields(false)
                      }}
                      disabled={isSubmitting}
                    >
                      {t('general.back')}
                    </ActionButton>
                  </FlowActions>
                </FlowPanel>
              )}
            </SectionCard>
          </div>

          <SecureFooter>
            <Lock width="11" height="11" color="#9CA3AF" />
            <span>
              {context?.companyName
                ? t('offerResponse.secureNotice', {
                    company: context.companyName,
                  })
                : t('offerResponse.secureNoticeGeneric')}
            </span>
          </SecureFooter>
        </OfferContent>
      </OfferScroll>
    </OfferPage>
  )
}

function AttachmentCard({ label, description, url, accentColor }) {
  const { t } = useTranslation()
  return (
    <AttachmentBox>
      <AttachmentTopRow>
        <FileIconWrapper accentcolor={accentColor}>
          <FileEarmarkText width="17" height="17" color={accentColor} />
        </FileIconWrapper>
        <div style={{ minWidth: 0 }}>
          <FileName>{label}</FileName>
          <FileMeta>{t('offerResponse.fileMeta', { description })}</FileMeta>
        </div>
      </AttachmentTopRow>
      <ViewLink
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        accentcolor={accentColor}
      >
        {t('general.view')}
        <ArrowRight width={14} height={14} color="currentColor" />
      </ViewLink>
    </AttachmentBox>
  )
}

AttachmentCard.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  accentColor: PropTypes.string.isRequired,
}

export default OfferResponse
